import clsx from 'clsx';
import {twMerge} from 'tailwind-merge';

import {ValidColorT} from '@/lib/types/colors.types';
import {AllIconNamesT} from '@/lib/types/icons/all-icons';
import {IconLibraryT, IconNameByLibraryT} from '@/lib/types/icons/libraries.types';
import {ICONS_BY_LIBRARY} from '@/lib/types/icons/names.types';

import {COLOR_CONFIGS} from '@/lib/color-configs';

type IconPropsT = {
  name: AllIconNamesT;
  className?: string;
  size?: number | 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  animate?: 'spin' | 'pulse' | 'bounce' | 'ping' | 'bell' | 'wase' | 'spin-slow' | 'slow-ping';
  rotate?: '0' | '45' | '90' | '180' | '270';
  flip?: 'horizontal' | 'vertical';
  color?: ValidColorT;
  inheritColor?: boolean;
  hoverColor?: ValidColorT;
  activeColor?: ValidColorT;
  variant?: 'solid' | 'outline' | 'ghost' | 'gradient' | 'basic';
  fixedWidth?: boolean;
  isInButton?: boolean;
  isActive?: boolean;
};

function isTablerIcon(name: string): name is IconNameByLibraryT['tabler'] {
  return ICONS_BY_LIBRARY.TablerIconName.includes(name as IconNameByLibraryT['tabler']);
}

function isBoxIcon(name: string): name is IconNameByLibraryT['box'] {
  return ICONS_BY_LIBRARY.BoxIconName.includes(name as IconNameByLibraryT['box']);
}

function isBootstrapIcon(name: string): name is IconNameByLibraryT['bootstrap'] {
  return ICONS_BY_LIBRARY.BootstrapIconName.includes(name as IconNameByLibraryT['bootstrap']);
}

function isRemixIcon(name: string): name is IconNameByLibraryT['remix'] {
  return ICONS_BY_LIBRARY.RemixIconName.includes(name as IconNameByLibraryT['remix']);
}

function isFeatherIcon(name: string): name is IconNameByLibraryT['feather'] {
  return ICONS_BY_LIBRARY.FeatherIconName.includes(name as IconNameByLibraryT['feather']);
}

function Icon({
  name,
  className = '',
  size = 'base',
  animate,
  rotate,
  flip,
  color = 'primary',
  inheritColor = false,
  hoverColor = COLOR_CONFIGS[color]?.solid?.iconHover,
  activeColor,
  variant = 'solid',
  fixedWidth = true,
  isInButton = false,
  isActive = false,
}: IconPropsT) {
  const getIconLibrary = (): IconLibraryT => {
    if (!name) throw new Error('Icon name is required');

    // Check each library for the icon name
    if (isTablerIcon(name)) return 'tabler';
    if (isBoxIcon(name)) return 'box';
    if (isBootstrapIcon(name)) return 'bootstrap';
    if (isRemixIcon(name)) return 'remix';
    if (isFeatherIcon(name)) return 'feather';

    // Default to tabler if not found
    return 'tabler';
  };

  const getIconClass = () => {
    const iconLibrary = getIconLibrary();

    // Base icon class based on library
    const libraryClasses = {
      tabler: `ti ti-${name}`,
      bootstrap: `bi bi-${name}`,
      remix: `ri-${name}`,
      box: `bx bx-${name}`,
      feather: `fe fe-${name}`,
    }[iconLibrary];

    let colorClasses;
    if (isInButton) {
      // When inside a button, use button context for default behavior
      colorClasses = {
        base: inheritColor ? '' : isActive ? `text-${activeColor || 'white'}` : `text-${color}`,
        hover: inheritColor ? '' : `group-hover:text-${hoverColor}`,
      };
    } else {
      // Standalone icon behavior
      colorClasses = {
        base: variant === 'outline' ? `text-${color} border border-current` : `text-${color}`,
        hover: `hover:text-${hoverColor}`,
      };
    }

    return twMerge(
      clsx(
        libraryClasses,
        // Core classes - updated for better scaling
        'inline-flex items-center justify-center leading-[1] transition-colors',
        // Only apply fixed width when not in a button or when explicitly requested
        fixedWidth && !isInButton && ['w-[1.5em]', 'text-center'],
        // For buttons, use flex alignment instead of fixed width
        isInButton && 'flex-shrink-0 flex-grow-0',
        // Size classes
        typeof size === 'string' && `text-${size}`,
        // Color classes
        colorClasses?.base,
        colorClasses?.hover,
        // Animation
        animate && `animate-${animate}`,
        // Rotation
        rotate && `rotate-${rotate}`,
        // Flip
        flip === 'horizontal' && 'scale-x-[-1]',
        flip === 'vertical' && 'scale-y-[-1]',
        // Additional classes from props
        className
      )
    );
  };

  // Button style
  const style = typeof size === 'number' ? {fontSize: `${size}px`} : undefined;

  return <i className={getIconClass()} style={style} />;
}

export type {IconPropsT};
export default Icon;
